<template>
  <div
    v-if="rates.length"
    ref="wrapperRef"
    class="lpu-reviews-list"
    data-qa="lpu_reviews_list"
  >
    <CoreImageLightbox ref="lightboxRef" />
    <PatientProfileModal
      :is-visible="isProfileVisible"
      :patient-data="patientProfile"
      @close="closePatientProfile"
    />

    <ReviewCard
      v-for="rate in visibleRates"
      :key="rate.id"
      :rate="rate"
      :lpu="lpu"
      @open-image="onOpenImage"
      @open-author="openPatientProfile"
    />
    <div
      v-if="hiddenRates.length"
      v-show="isExpanded"
    >
      <ReviewCard
        v-for="rate in hiddenRates"
        :key="rate.id"
        :rate="rate"
        :lpu="lpu"
        @open-image="onOpenImage"
        @open-author="openPatientProfile"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import CoreImageLightbox from 'src/core/CoreImageLightbox/CoreImageLightbox.vue'
import type { CoreImageLightboxItem } from 'src/core/CoreImageLightbox/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'
import ReviewCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCard.vue'
import PatientProfileModal from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/PatientProfile/PatientProfileModal.vue'
import usePatientProfileModal from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/composables/usePatientProfileModal'
import useReviewsViewTracking from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/composables/useReviewsViewTracking'
import useScrollToReview from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/composables/useScrollToReview'
import { VISIBLE_RATES_LIMIT } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getPageRates'

type LightboxExpose = {
  open: (_index: number, _items?: CoreImageLightboxItem[]) => void
}

const props = defineProps<{
  rates: Rate[]
  lpu: Lpu
  isExpanded: boolean
}>()

const emit = defineEmits(['expand'])

const wrapperRef = ref<HTMLElement | null>(null)
const lightboxRef = ref<LightboxExpose | null>(null)
const isExpandedRef = toRef(props, 'isExpanded')
const visibleRates = computed(() => props.rates.slice(0, VISIBLE_RATES_LIMIT))
const hiddenRates = computed(() => props.rates.slice(VISIBLE_RATES_LIMIT))

useReviewsViewTracking(wrapperRef, isExpandedRef)
useScrollToReview({
  getReviewIds: () => props.rates.map(rate => rate.id),
  visibleCount: VISIBLE_RATES_LIMIT,
  expand: () => emit('expand'),
})

const {
  isVisible: isProfileVisible,
  patientData: patientProfile,
  open: openPatientProfile,
  close: closePatientProfile,
} = usePatientProfileModal()

function onOpenImage(payload: { items: CoreImageLightboxItem[], index: number }) {
  lightboxRef.value?.open(payload.index, payload.items)
}
</script>
